import type { EditorHost } from '@blocksuite/block-std';
import { WithDisposable } from '@blocksuite/block-std';
import type {
  AffineAIPanelState,
  AffineAIPanelWidget,
  EdgelessRootService,
  SurfaceBlockModel,
} from '@blocksuite/blocks';
import {
  ConnectorLabelOffsetAnchor,
  EdgelessEditorBlockSpecs,
  fitContent,
  FontStyle,
  ShapeElementModel,
} from '@blocksuite/blocks';
import { AffineSchemas } from '@blocksuite/blocks/schemas';
import type { SerializedXYWH } from '@blocksuite/global/utils';
import type { Doc } from '@blocksuite/store';
import { DocCollection, nanoid, Schema } from '@blocksuite/store';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import mermaid from 'mermaid';

import { ImageSourceManager } from '../image-sources/manager';
import type {
  BaseConnectorStyle,
  BaseShapeStyle,
  MermaidNode,
  MermaidTemplate,
} from '../mermaid/template';
import {
  createConnectorNode,
  createMermaidTemplate,
  createShapeNode,
  defaultTheme,
  isConnectorNode,
  isShapeNode,
  ShapeType,
} from '../mermaid/template';
import { getAIPanel } from '../utils/edgeless';

// Initialize mermaid with secure defaults and better styling
mermaid.initialize({
  startOnLoad: false,
  securityLevel: 'strict',
  // Improve diagram layout and sizing
  htmlLabels: true,
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
    padding: 20,
  },
  mindmap: {
    padding: 20,
  },
  sequence: {
    useMaxWidth: true,
    width: 800,
  },
  // Ensure diagrams are centered and properly sized
  themeVariables: {
    'mindmap-padding': 20,
    'mindmap-spacing': 100,
  },
});

function formatNodeText(text: string): string {
  const words = text.split(' ');

  // For shorter texts (like connector labels), don't add line breaks for texts under 15 chars
  if (text.length <= 15) {
    return text;
  }

  // For medium-length texts, just split into two lines
  if (text.length <= 30 && words.length >= 2) {
    // Find the middle to split evenly
    const middle = Math.floor(words.length / 2);
    const firstHalf = words.slice(0, middle).join(' ');
    const secondHalf = words.slice(middle).join(' ');
    return `${firstHalf}\n${secondHalf}`;
  }

  // For longer texts, chunk into multiple lines with ~15-20 chars per line
  const maxCharsPerLine = 20; // Increased from 18 to 20 for better fit with background
  let currentLine = '';
  const lines: string[] = [];

  for (const word of words) {
    if (
      (currentLine + ' ' + word).length > maxCharsPerLine &&
      currentLine.length > 0
    ) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = currentLine.length === 0 ? word : currentLine + ' ' + word;
    }
  }

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  return lines.join('\n');
}

export class MermaidBuilder {
  private vectorCandidates: Array<{ nodeId: string; searchTerm: string }> = [];

  constructor(_host: EditorHost) { }

  private cleanMermaidInput(text: string): string {
    let cleaned = text.trim();

    // Try to parse as JSON first (for the new response format)
    try {
      // Check if it's valid JSON with a mermaid property
      const jsonResponse = JSON.parse(cleaned);
      if (
        jsonResponse &&
        typeof jsonResponse === 'object' &&
        jsonResponse.mermaid
      ) {

        // Store the vector candidates if available
        if (Array.isArray(jsonResponse.vectorCandidates)) {
          this.vectorCandidates = jsonResponse.vectorCandidates;
        }

        // Return only the mermaid content
        cleaned = jsonResponse.mermaid;
      }
    } catch (e) {
      // Not JSON, continue with normal processing
    }

    // Handle standard mermaid code blocks
    if (cleaned.startsWith('```')) {
      cleaned = cleaned
        .replace(/^```mermaid\n/, '')
        .replace(/```$/, '')
        .trim();
    }

    const lines = cleaned.split('\n');
    const processedLines = lines.map(line => {
      if (
        line.trim().startsWith('flowchart') ||
        line.trim().startsWith('graph')
      ) {
        return line;
      }
      return line.replace(
        /$begin:math:display$(.*?)$end:math:display$/g,
        (_match, content) => {
          const escaped = content
            .replace(/[()]/g, '_')
            .replace(/[&]/g, 'and')
            .replace(/[-]/g, ' ')
            .replace(/[^a-zA-Z0-9_ ]/g, '')
            .trim();
          return `[${escaped}]`;
        }
      );
    });

    const result = processedLines.join('\n');
    return result;
  }

  private async parseMermaidSyntax(text: string): Promise<{
    type: string;
    svg: Document;
    originalSvg: string;
  }> {
    let type = 'flowchart';

    const cleanedText = this.cleanMermaidInput(text);

    try {
      type = mermaid.detectType(cleanedText);
      await mermaid.parse(cleanedText);
    } catch (err) {
      console.warn(
        'First parse attempt failed, trying with simplified syntax:',
        err
      );
    }

    let { svg } = await mermaid.render('graphDiv', cleanedText);

    // Replace &quot; with actual quotes in the SVG string
    svg = svg.replace(/&quot;/g, '"');

    const parsedSvg = new DOMParser().parseFromString(svg, 'image/svg+xml');
    return { type, svg: parsedSvg, originalSvg: svg };
  }

  private getShapeType(nodeElement: Element): ShapeType {
    // Check for circle nodes (like Start/End)
    const circle = nodeElement.querySelector('circle');
    if (circle) return ShapeType.Ellipse;

    // Check for parallelogram nodes (like Input/Output)
    const polygon = nodeElement.querySelector('polygon');
    if (polygon) {
      const points = polygon.getAttribute('points');
      if (points) {
        const pointArray = points.split(' ');
        if (pointArray.length === 4) {
          const p1 = pointArray[0];
          if (p1 && (p1.startsWith('-19.5,0') || p1.startsWith('0,-'))) {
            return ShapeType.Triangle;
          }
        }
        if (points.includes(',0 ') && points.includes('-')) {
          // TODO: fix correctly parsing diamond shape
          // return ShapeType.Diamond;
          return ShapeType.Rect;
        }
      }
    }

    // Default rectangular shapes
    const rect = nodeElement.querySelector('rect');
    if (rect) {
      return ShapeType.Rect;
    }

    return ShapeType.Rect;
  }

  private extractNodeInfo(nodeElement: Element): {
    id: string;
    text: string;
    xywh: SerializedXYWH;
    shapeType: ShapeType;
  } {
    const nodeId = nodeElement.getAttribute('id') || nanoid();
    const nodeNameMatch = /^flowchart-(.*?)-\d+$/.exec(nodeId);
    const nodeName = nodeNameMatch?.[1] ?? nodeId;

    const transform = nodeElement.getAttribute('transform') || '';
    const rect = nodeElement.querySelector('rect');
    const fo = nodeElement.querySelector(
      'foreignObject > div > span.nodeLabel > p'
    );
    const label = fo?.textContent?.trim() || nodeName;
    const formattedLabel = formatNodeText(label);

    let cx = 0;
    let cy = 0;
    const transformMatch = /translate\(([-\d.]+),\s*([-\d.]+)\)/.exec(
      transform
    );
    if (transformMatch) {
      const xVal = transformMatch[1];
      const yVal = transformMatch[2];
      if (xVal !== undefined) cx = Number.parseFloat(xVal);
      if (yVal !== undefined) cy = Number.parseFloat(yVal);
    }

    let width = 160;
    let height = 60;
    if (rect) {
      width = Number.parseFloat(rect.getAttribute('width') || '160');
      height = Number.parseFloat(rect.getAttribute('height') || '60');
    }

    // For ellipse shapes, make height equal to width to ensure a perfect circle
    const shapeType = this.getShapeType(nodeElement);
    if (shapeType === ShapeType.Ellipse) {
      height = width;
    }

    const xywh = `[${cx},${cy},${width},${height}]` as SerializedXYWH;

    return {
      id: `flowchart-${nodeName}`,
      text: formattedLabel,
      xywh,
      shapeType,
    };
  }

  private extractEdgesInfo(svg: Document): MermaidNode[] {
    const edgeElements = Array.from(svg.querySelectorAll('.edgePaths > path'));
    const edges: MermaidNode[] = [];

    for (const edgeElement of edgeElements) {
      const edgeId = edgeElement.getAttribute('id') || '';
      const parts = edgeId.split('_');
      if (parts.length >= 3) {
        // The last part is the numeric edge index
        const edgeNumber = parts[parts.length - 1] ?? '';

        // The first part is 'L'; the second part is our source name
        const sourceName = (parts[1] ?? '').trim();

        // Everything from the third part up to the second-last belongs to the target name
        const targetName = parts.slice(2, -1).join('_').trim();

        const sourceId = `flowchart-${sourceName}`;
        const targetId = `flowchart-${targetName}`;

        // Count how many edges come from this source
        const sourceEdges = edgeElements.filter(edge => {
          const eId = edge.getAttribute('id') || '';
          const eParts = eId.split('_');
          if (eParts.length < 3) return false;
          // Remove the last element, which should be the numeric index
          eParts.pop();
          const eSourceName = (eParts[1] ?? '').trim();
          return eSourceName === sourceName;
        });

        // Find corresponding label by matching edge ID pattern
        const labelSelector = `.edgeLabels > g:nth-child(${parseInt(edgeNumber, 10) + 1
          })`;
        const labelGroup = svg.querySelector(labelSelector);
        const labelElement = labelGroup?.querySelector('.edgeLabel p');

        // Get the label text and format it
        let labelText = labelElement?.textContent?.trim() || '';
        if (labelText) {
          labelText = formatNodeText(labelText);
        }

        // Extract label dimensions from the SVG
        let labelXYWH: [number, number, number, number] | undefined;
        if (labelGroup) {
          const transform = labelGroup.getAttribute('transform');
          const translateMatch = transform?.match(
            /translate\(([-\d.]+),\s*([-\d.]+)\)/
          );
          if (translateMatch) {
            const x = translateMatch[1];
            const y = translateMatch[2];
            const foreignObject = labelGroup.querySelector('foreignObject');
            if (foreignObject && x !== undefined && y !== undefined) {
              const width = parseFloat(
                foreignObject.getAttribute('width') || '0'
              );

              // If the label has line breaks, adjust height accordingly
              const lineCount = labelText.split('\n').length;
              const adjustedHeight = Math.max(20, lineCount * 18);

              // Calculate a more appropriate width based on the content
              const longestLine = labelText
                .split('\n')
                .reduce((max, line) => Math.max(max, line.length), 0);
              const estimatedWidth = Math.min(
                Math.max(60, longestLine * 7), // min 60px
                100 // max 100px
              );

              labelXYWH = [
                parseFloat(x) - (width * 0.7) / 2,
                parseFloat(y),
                estimatedWidth,
                adjustedHeight,
              ];
            }
          }
        }

        // Create connector node as before
        const edge = createConnectorNode(
          sourceId,
          targetId,
          defaultTheme,
          edges.length,
          labelText,
          sourceEdges.length,
          labelXYWH
        );

        edges.push(edge);
      }
    }

    return edges;
  }

  // A small helper to compare node text vs. candidate nodeId by splitting on words, ignoring punctuation/case
  private wordByWordMatch(nodeLabel: string, candidateNodeId: string): boolean {
    // strip punctuation, lower, split
    const normalize = (s: string) =>
      s
        .toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter(Boolean);

    const labelWords = normalize(nodeLabel);
    const candidateWords = normalize(candidateNodeId);
    // must have same word count
    if (labelWords.length !== candidateWords.length) return false;
    // check each word
    for (let i = 0; i < labelWords.length; i++) {
      if (labelWords[i] !== candidateWords[i]) return false;
    }
    return true;
  }

  async process(text: string): Promise<MermaidTemplate> {
    const { type, svg, originalSvg } = await this.parseMermaidSyntax(text);

    const nodeElements = Array.from(svg.querySelectorAll('.node'));
    const nodes: MermaidNode[] = [];

    // We will store multiple matches in a map: nodeId -> vectorURL
    const vectorMap = new Map<string, string>();

    // We'll store each node by its raw label for matching, then still use the formatted text for the shape.
    const nodeTextMap = new Map<
      string,
      {
        element: Element;
        info: {
          id: string;
          text: string;
          xywh: SerializedXYWH;
          shapeType: ShapeType;
        };
      }
    >();

    // First pass: extract all node info and build the map
    for (const nodeElement of nodeElements) {
      const nodeInfo = this.extractNodeInfo(nodeElement);
      // We also get the raw label (unformatted) for matching
      const fo = nodeElement.querySelector(
        'foreignObject > div > span.nodeLabel > p'
      );
      const rawLabel = fo?.textContent?.trim() || nodeInfo.id;

      // Map the raw label -> node info
      nodeTextMap.set(rawLabel, { element: nodeElement, info: nodeInfo });
    }

    // Check if we have vector candidates to match
    if (this.vectorCandidates.length > 0) {

      // For each candidate, try to match all node raw labels
      for (const candidate of this.vectorCandidates) {
        for (const [rawText, { info }] of nodeTextMap.entries()) {
          if (this.wordByWordMatch(rawText, candidate.nodeId)) {
            // Attempt to fetch a vector
            try {
              const searchTerm = candidate.searchTerm.trim();
              if (searchTerm && searchTerm.length > 2) {
                const manager = ImageSourceManager.getInstance();
                // Initial search
                let results = await manager.searchVectors({
                  query: searchTerm,
                });

                // If initial search failed and searchTerm has multiple words, retry with the first word
                // TODO: Do a better version of several keyword searches per vector node
                if (
                  (!results || results.length === 0) &&
                  searchTerm.includes(' ')
                ) {
                  const firstWord = searchTerm.split(' ')[0];
                  // Ensure the first word is valid before retrying
                  if (firstWord && firstWord.length > 2) {
                    results = await manager.searchVectors({
                      query: firstWord,
                    });
                  }
                }

                // Process results if found (either initially or after retry)
                const firstResult = results?.[0];
                if (firstResult) {
                  vectorMap.set(info.id, firstResult.url);
                }
              }
            } catch (error) {
              console.error('Error fetching vector illustration:', error);
            }
          }
        }
      }
    }

    // Create shape nodes for all elements
    for (const nodeElement of nodeElements) {
      const { id, text, xywh, shapeType } = this.extractNodeInfo(nodeElement);
      const node = createShapeNode(
        id,
        text,
        nodes.length,
        defaultTheme,
        undefined,
        xywh,
        shapeType
      );
      nodes.push(node);
    }

    // Add edges
    const edges = this.extractEdgesInfo(svg);
    nodes.push(...edges);

    const template = createMermaidTemplate(type, nodes);

    // Store the vector map for use at render time
    (template as any).vectorMap = Object.fromEntries(vectorMap);

    return {
      ...template,
      originalSvg,
      vectorIllustration: null,
      vectorNodeText: null,
    };
  }

  static create(host: EditorHost): MermaidBuilder {
    return new MermaidBuilder(host);
  }
}

export const createMermaidDataProcessor: (
  host: EditorHost,
  ctx: {
    get(): Record<string, unknown>;
    set(data: Record<string, unknown>): void;
  }
) => NonNullable<AffineAIPanelWidget['config']>['answerRenderer'] = (
  host,
  ctx
) => {
    return (answer: string, state?: AffineAIPanelState) => {
      if (state === 'generating') {
        getAIPanel(host)?.generatingElement?.updateLoadingProgress?.(2);
        return nothing;
      }

      if (state !== 'finished' && state !== 'error') {
        return nothing;
      }

      return html`<style>
        .mermaid-container {
          width: 100%;
          height: 300px;
        }
      </style>
      <div class="mermaid-container">
        <ai-mermaid-renderer
          .host=${host}
          .ctx=${ctx}
          .text=${answer}
        ></ai-mermaid-renderer>
      </div>`;
    };
  };

@customElement('ai-mermaid-renderer')
export class AIMermaidRenderer extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      --mermaid-shape-yellow: var(--affine-palette-shape-yellow);
      --mermaid-shape-blue: var(--affine-palette-shape-blue);
      --mermaid-shape-green: var(--affine-palette-shape-green);
      --mermaid-shape-pink: var(--affine-palette-shape-pink);
      --mermaid-connector-color: var(--affine-text-primary-color);
      --mermaid-text-color: var(--affine-text-primary-color);
    }
  `;

  private readonly _editorContainer: Ref<HTMLDivElement> =
    createRef<HTMLDivElement>();
  private _doc!: Doc;
  private _templateReadyResolve!: (value: void) => void;
  private readonly _templateReady = new Promise<void>(resolve => {
    this._templateReadyResolve = resolve;
  });
  // Track created elements for viewport adjustment
  private readonly _createdElementIds: string[] = [];
  private readonly _groupId: string | null = null;

  @query('editor-host')
  private accessor _editorHost!: EditorHost;

  @property({ attribute: false })
  accessor text!: string;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor ctx:
    | {
      get(): Record<string, unknown>;
      set(data: Record<string, unknown>): void;
    }
    | undefined = undefined;

  get templateReady() {
    return this._templateReady;
  }

  protected override firstUpdated() {
    requestAnimationFrame(() => {
      if (!this._editorHost) return;
      MermaidBuilder.create(this._editorHost)
        .process(this.text)
        .then((template: MermaidTemplate) => {
          if (this.ctx) {
            this.ctx.set({
              data: template,
              originalSvg: template.originalSvg,
              createdElementIds: this._createdElementIds,
              groupId: this._groupId,
            });
            getAIPanel(this.host)
              ?.shadowRoot?.querySelector('ai-panel-answer')
              ?.requestUpdate();
            this._templateReadyResolve();
          }
        })
        .catch(err => {
          console.error('Error processing Mermaid diagram:', err);
          this.ctx?.set({ error: 'Failed to process Mermaid diagram' });
          this._templateReadyResolve();
        });
    });
  }

  protected override render() {
    return html`<style>
        .mermaid-container {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .edgeless-container {
          width: 100%;
          height: 100%;
          border-radius: 4px;
          border: 1px solid var(--affine-border-color);
          background-color: var(--affine-background-primary-color);
        }

        .mask {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1;
          background-color: transparent;
          width: 100%;
          height: 100%;
        }
      </style>
      <div class="mermaid-container">
        <div
          class="edgeless-container affine-edgeless-viewport"
          ${ref(this._editorContainer)}
        >
          ${this.host.renderSpecPortal(this._doc, EdgelessEditorBlockSpecs)}
        </div>
        <div class="mask"></div>
      </div>`;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    const schema = new Schema().register(AffineSchemas);
    const collection = new DocCollection({
      schema,
      id: 'MERMAID_PREVIEW',
    });
    collection.meta.initialize();
    const doc = collection.createDoc();
    if (!doc) {
      console.error('[MermaidRenderer] Failed to create preview doc');
      return;
    }

    doc.load(() => {
      const pageBlockId = doc.addBlock('affine:page', {});
      doc.addBlock('affine:surface', {}, pageBlockId);
    });

    doc.resetHistory();
    this._doc = doc;
  }
}


export async function renderBlocksuiteElements(
  host: EditorHost,
  edgelessService: EdgelessRootService,
  surface: SurfaceBlockModel,
  template: MermaidTemplate
): Promise<string[]> {
  try {
    // Get the vector map from the template
    const vectorMap = (template as any).vectorMap || {};

    const shapeNodes = template.blocks.filter(isShapeNode);

    // Calculate the center of the diagram based on initial node positions
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    shapeNodes.forEach(block => {
      const xywh = block.xywh || '[0,0,160,60]';
      const [x, y, w, h] = JSON.parse(xywh.replace('[', '[').replace(']', ']'));
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });

    const centerX = minX + (maxX - minX) / 2;
    const centerY = minY + (maxY - minY) / 2;
    const scaleFactor = 1.3; // Increase spacing by 30%

    // Scale node positions and create elements
    const blockCreationPromises = shapeNodes.map(async block => {
      let xywh = block.xywh || '[0,0,160,60]';
      const style = block.meta?.style || ({} as BaseShapeStyle);
      const shapeType = block.shapeType || style.shapeType || ShapeType.Rect;

      // --- Start Scaling Logic ---
      const [x, y, w, h] = JSON.parse(xywh.replace('[', '[').replace(']', ']'));
      const nodeCenterX = x + w / 2;
      const nodeCenterY = y + h / 2;

      const scaledCenterX = centerX + (nodeCenterX - centerX) * scaleFactor;
      const scaledCenterY = centerY + (nodeCenterY - centerY) * scaleFactor;

      const scaledX = scaledCenterX - w / 2;
      const scaledY = scaledCenterY - h / 2;

      xywh = `[${scaledX},${scaledY},${w},${h}]` as SerializedXYWH;

      const additionalVerticalPadding =
        shapeType === ShapeType.Diamond || shapeType === ShapeType.Ellipse
          ? 20
          : 0;
      const additionalHorizontalPadding =
        shapeType === ShapeType.Diamond || shapeType === ShapeType.Ellipse
          ? 20
          : 0;

      // Check if we have a vector for this node
      const vectorUrl = vectorMap[block.id];
      let createdElementId: string | null = null;

      if (vectorUrl) {
        try {
          // Fetch the SVG content from the URL
          const response = await fetch(vectorUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const svgContent = await response.text();

          // Create a blob from the SVG content
          const blob = new Blob([svgContent], { type: 'image/svg+xml' });

          // Save the blob and get the sourceId
          const sourceId = await host.doc.blobSync.set(blob);

          // Use the scaled XYWH for the image block
          // We might want to adjust height/width calculation based on vector size later
          const [imgX, imgY, imgW, imgH] = JSON.parse(
            xywh.replace('[', '[').replace(']', ']')
          );

          // Define a scaling factor (0.3 for 30% size to better fit in diagram nodes)
          const scaleFactor = 0.3;
          const scaledWidth = imgW * scaleFactor;
          const scaledHeight = imgH * scaleFactor;

          // Use scaled dimensions for the image block's position and size
          const imageXYWH =
            `[${imgX}, ${imgY}, ${scaledWidth}, ${scaledHeight}]` as SerializedXYWH;

          // Create the image block with correct props
          createdElementId = edgelessService.addBlock(
            'affine:image',
            {
              sourceId: sourceId,
              width: scaledWidth, // Intrinsic width for the image block
              height: scaledHeight, // Intrinsic height for the image block
              xywh: imageXYWH, // Position and bounding box on the surface
              caption: block.text || '', // Use original node text as caption
              index: edgelessService.layer.generateIndex('affine:image'),
            },
            surface.id
          );
        } catch (error) {
          console.error(
            'Failed to fetch or process vector illustration, falling back to shape:',
            error
          );
          // Let createdElementId remain null, fallback shape will be created below
        }
      }

      // If not replacing with vector OR if vector processing failed, create a standard shape
      if (!createdElementId) {
        createdElementId = surface.addElement({
          type: 'shape',
          xywh, // Use scaled position
          text: block.text,
          color: style.color,
          shapeType,
          fillColor: style.fillColor,
          strokeWidth: style.strokeWidth,
          strokeColor: style.strokeColor,
          filled: style.filled,
          radius: style.radius,
          fontStyle: FontStyle.Normal,
          padding: [
            additionalVerticalPadding + 10,
            additionalHorizontalPadding + 10,
          ],
        });
      }

      // Return mapping information for later use
      return { originalId: block.id, createdId: createdElementId };
    });

    // Wait for all block creation promises to resolve
    const createdElementsMapData = await Promise.all(blockCreationPromises);

    // Build the map of original block IDs to created element IDs and the list of element IDs
    const shapeIdMap = new Map<string, string>();
    const elementIds: string[] = [];
    createdElementsMapData.forEach(item => {
      if (item.createdId) {
        shapeIdMap.set(item.originalId, item.createdId);
        elementIds.push(item.createdId);
      } else {
        console.warn(
          `Failed to create element for original block ID: ${item.originalId}`
        );
      }
    });

    // Let the layout calculations settle
    await new Promise(resolve => setTimeout(resolve, 200));

    // Track connection counts between node pairs
    const connectionCounts = new Map<string, number>();

    // Create connectors using theme values without overrides
    const connectorElements = template.blocks
      .filter(isConnectorNode)
      .map(block => {
        const style = block.meta?.style as BaseConnectorStyle;
        const sourceId = block.meta?.parent
          ? shapeIdMap.get(block.meta.parent)
          : undefined;

        // For target:
        // Example id: "connector_flowchart-A_flowchart-flowchart-B"
        // parts[1] = "flowchart-A", parts[2] = "flowchart-flowchart-B"
        const parts = block.id.split('_');
        let targetPart = parts[2] ?? '';
        // Remove one "flowchart-" prefix from the targetPart if it appears twice
        targetPart = targetPart.replace(/^flowchart-flowchart-/, 'flowchart-');
        // Now targetPart should be "flowchart-B"
        const targetId = shapeIdMap.get(targetPart);

        if (!sourceId || !targetId) return null;

        // Get and increment connection count for this node pair
        const connectionKey = `${sourceId}-${targetId}`;
        const connectionCount = connectionCounts.get(connectionKey) || 0;
        connectionCounts.set(connectionKey, connectionCount + 1);

        const connectorElement = surface.addElement({
          type: 'connector',
          source: {
            id: sourceId,
            position: [0.5, 1], // Connect from bottom center of source
          },
          target: {
            id: targetId,
            position: [0.5, 0], // Connect to top center of target
          },
          mode: style.mode,
          stroke: style.stroke,
          strokeWidth: style.strokeWidth,
          strokeStyle: style.strokeStyle,
          frontEndpointStyle: style.frontEndpointStyle,
          rearEndpointStyle: style.rearEndpointStyle,
          rough: style.rough || false,
          roughness: style.roughness || 1.4,
          controllers: [],
          text: block.text,
          labelDisplay: style.labelDisplay ?? true,
          labelEditing: false,
          labelOffset: style.labelOffset || {
            distance: 0.5,
            anchor: ConnectorLabelOffsetAnchor.Center,
          },
          labelXYWH: style.labelXYWH,
          labelStyle: {
            ...style.labelStyle,
            color: '--affine-text-primary-color',
            fontFamily: 'Inter',
            fontSize: 12,
            fontStyle: FontStyle.Normal,
            fontWeight: 400,
            textAlign: 'center',
          },
          labelConstraints: style.labelConstraints || {
            hasMaxWidth: true,
            maxWidth: 120,
          },
        });
        return connectorElement;
      })
      .filter((el): el is NonNullable<typeof el> => el !== null);

    const elements = [...elementIds, ...connectorElements];

    // Fit content for shape elements
    host.doc.transact(() => {
      elementIds.forEach(elementId => {
        const nodeElement = edgelessService.getElementById(elementId);
        // Check if it's a ShapeElementModel before fitting content
        if (nodeElement instanceof ShapeElementModel) {
          fitContent(nodeElement);
        }

        // move nodes to the front
        const newIndex = edgelessService.layer.generateIndex('connector');
        edgelessService.updateElement(elementId, {
          index: newIndex,
        });
      });
    });

    return elements;
  } catch (err) {
    console.error('Error creating Mermaid diagram:', err);
    throw err;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-mermaid-renderer': AIMermaidRenderer;
  }
}
